import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { ContentViewerActions } from '@/content-viewer/store/slice'

/** What the chat side panel is showing: the conversation, or the open source. */
export enum ChatPanelTabType {
  Assistant = 'assistant',
  Source = 'source',
}

/** View state for the document-first draft workspace, keyed by draft matter artifact version id. */
export type DraftWorkspaceState = {
  /* Whether the chat panel is open (vs collapsed), per draft. Absent = open by default. */
  chatPanelOpenByDraftArtifactVersionId: Partial<{ [draftArtifactVersionId: string]: boolean }>
  /* The selected chat panel tab, per draft. Absent = the conversation. */
  chatPanelTabByDraftArtifactVersionId: Partial<{ [draftArtifactVersionId: string]: ChatPanelTabType }>
  /* The footnote number the open draft shows for each source it cites with a marker, keyed by agentV2SourceKey. Empty when no draft is open. */
  draftFootnoteNumberBySourceKey: Partial<{ [sourceKey: string]: number }>
}

const initialState: DraftWorkspaceState = {
  chatPanelOpenByDraftArtifactVersionId: {},
  chatPanelTabByDraftArtifactVersionId: {},
  draftFootnoteNumberBySourceKey: {},
}

export const draftWorkspaceSlice = createSlice({
  name: 'draftWorkspaceState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    /** Open or collapse the chat panel for a draft. */
    setChatPanelOpen: (state, action: PayloadAction<{ draftArtifactVersionId: string; open: boolean }>) => {
      state.chatPanelOpenByDraftArtifactVersionId[action.payload.draftArtifactVersionId] = action.payload.open
    },

    /** Show the conversation or the open source in a draft's chat panel. */
    setChatPanelTab: (state, action: PayloadAction<{ draftArtifactVersionId: string; tab: ChatPanelTabType }>) => {
      state.chatPanelTabByDraftArtifactVersionId[action.payload.draftArtifactVersionId] = action.payload.tab
    },

    /** Record the footnote numbers the open draft gives its sources. */
    setDraftFootnoteNumbers: (state, action: PayloadAction<{ footnoteNumberBySourceKey: Partial<{ [sourceKey: string]: number }> }>) => {
      state.draftFootnoteNumberBySourceKey = action.payload.footnoteNumberBySourceKey
    },

    /** Forget the draft's footnote numbers once it closes. */
    clearDraftFootnoteNumbers: (state) => {
      state.draftFootnoteNumberBySourceKey = {}
    },
  },
  extraReducers: (builder) => {
    // One source is open at a time, so once it is closed no draft can be on its tab, whichever control closed it.
    builder.addCase(ContentViewerActions.clearContentViewer, (state) => {
      state.chatPanelTabByDraftArtifactVersionId = {}
    })
  },
})

export const DraftWorkspaceActions = draftWorkspaceSlice.actions

export default draftWorkspaceSlice.reducer
